import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, COMPANY, ZONES } from "../../data/site";

const BUSINESS_ID = `${SITE_URL}/#entreprise`;

export const businessSchema = {
  "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
  "@id": BUSINESS_ID,
  name: `${COMPANY.legalName} - ${COMPANY.brand}`,
  alternateName: [COMPANY.brand, COMPANY.legalName],
  description: "Entreprise spécialisée dans le traitement anti-moustique à Bordeaux et en Gironde : recherche et élimination des gîtes larvaires, nébulisation et atomiseur de l'extérieur avec biocides professionnels.",
  url: `${SITE_URL}/`,
  image: `${SITE_URL}/images/og-moustique-bordeaux.jpg`,
  logo: `${SITE_URL}/images/logo-termite-habitat-33.png`,
  telephone: COMPANY.phoneIntl,
  email: COMPANY.email,
  address: { "@type": "PostalAddress", streetAddress: COMPANY.street, postalCode: COMPANY.postalCode, addressLocality: COMPANY.city, addressRegion: "Nouvelle-Aquitaine", addressCountry: "FR" },
  areaServed: [{ "@type": "AdministrativeArea", name: "Gironde" }, ...ZONES.filter(z => z.slug !== "gironde").map(z => ({ "@type": "Place", name: z.name }))],
  founder: { "@type": "Person", name: "Marc Barbier" },
  sameAs: [COMPANY.facebook, "https://www.termitehabitat33.fr/", "https://www.termitebordeaux.fr/"],
  knowsAbout: ["Traitement anti-moustique", "Moustique tigre", "Démoustication", "Gîtes larvaires", "Nébulisation"],
};

export default function Seo({ title, description, path = "/", faq = [], breadcrumb = [], service, article, image, noindex = false }) {
  // Drop the static homepage fallback tags from index.html once per-route tags render
  useEffect(() => { document.querySelectorAll('head [data-rh="true"]').forEach(el => el.remove()); }, []);
  const url = `${SITE_URL}${path === "/" ? "/" : path}`;
  const img = `${SITE_URL}${image || "/images/og-moustique-bordeaux.jpg"}`;
  const graph = [
    businessSchema,
    { "@type": "WebSite", "@id": `${SITE_URL}/#site`, url: `${SITE_URL}/`, name: "Moustique Bordeaux", inLanguage: "fr-FR", publisher: { "@id": BUSINESS_ID } },
    { "@type": "WebPage", "@id": `${url}#page`, url, name: title, description, inLanguage: "fr-FR", isPartOf: { "@id": `${SITE_URL}/#site` }, about: { "@id": BUSINESS_ID }, primaryImageOfPage: img },
  ];
  if (article) {
    graph.push({ "@type": "Article", headline: title, description, image: img, inLanguage: "fr-FR", mainEntityOfPage: `${url}#page`, author: { "@id": BUSINESS_ID }, publisher: { "@id": BUSINESS_ID }, datePublished: article.date, dateModified: article.date });
  }
  if (breadcrumb.length) {
    graph.push({
      "@type": "BreadcrumbList",
      itemListElement: [{ name: "Accueil", path: "/" }, ...breadcrumb].map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.name, item: `${SITE_URL}${b.path}` })),
    });
  }
  if (service) {
    graph.push({ "@type": "Service", name: service.name, serviceType: "Traitement anti-moustique", provider: { "@id": BUSINESS_ID }, areaServed: service.area || "Gironde", description: service.description || description });
  }
  if (faq.length) {
    graph.push({ "@type": "FAQPage", mainEntity: faq.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
  }
  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {!noindex && <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@graph": graph })}</script>}
    </Helmet>
  );
}
